import React, { useState, useEffect } from "react";
import styles from "./JobList.module.css";
import Pagination from "../Pagination/Pagination";
import Link from "next/link";

interface Job {
  id: number;
  category: string;
  country: string;
  slug: any;
  banner: {
    title: string;
    description: string;
  };
}

interface JobListProps {
  Data: Job[];
  filterData: { category?: string; country?: string };
}

const JobList: React.FC<JobListProps> = ({ Data, filterData }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Filter the job data based on filterData props
  const filteredJobs = Data.filter((job) => {
    const matchCategory = filterData.category
      ? job.category === filterData.category
      : true;
    const matchCountry = filterData.country
      ? job.country === filterData.country
      : true;
    return matchCategory && matchCountry;
  });

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterData]);

  // Calculate total pages
  const totalItems = filteredJobs.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Ensure current page is valid
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Check if any filter is active
  const isFiltered = filterData.category || filterData.country;

  // Pagination calculation
  const startIndex =
    totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);
  const displayedJobs = filteredJobs.slice(startIndex - 1, endIndex);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Determine if pagination should be shown
  const shouldShowPagination = isFiltered
    ? totalItems > itemsPerPage // Show only if filtered results have more than 5 items
    : totalItems > 0; // Show for unfiltered data if there are any items

  return (
    <div className="container">
      <div className={styles.container}>
        <div className={styles.header}>
          {totalItems > 0
            ? `Showing ${startIndex} - ${endIndex} of ${totalItems} jobs in All Locations`
            : "No jobs found"}
        </div>

        {displayedJobs.map((job, index) => (
          <Link key={index} href={`/vacancies/${job?.slug}`}>
            <div key={job.id} className={styles?.jobCard}>
              <div className={styles?.jobTitle}>{job?.banner?.title}</div>
              <div className={styles?.jobDetails}>
                <p>{job.banner?.description}</p>
              </div>
            </div>
          </Link>
        ))}

        {shouldShowPagination && (
          <div className={styles.paginationContainer}>
            <Pagination
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              greenStyle
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default JobList;
